import React, { useEffect, useState } from "react";
import "./Profile.scss";
import { DeleteOutlined } from "@ant-design/icons";
import Personal from "../Personal/Personal";
import About from "../About/About";
import Experience from "../Experience/Experience";
import Education from "../Education/Education";
import Skill from "../AdminDashboard/Skill/Skill";
import Certificate from "../Certificate/Certificate";
import Language from "../Language/Language";
import ProfileModal from "../../components/ProfileModal/ProfileModal";
import userApi from "services/UserApi";
import Helpers from "@utils/Helpers";
import { TUserInfo } from "@utils/Interfaces";
import { useUser } from "context/UserContext";
import { AxiosResponse } from "axios";

const USER_PROFILE_ENDPOINT = userApi.updateUserProfile;
const USER_SKILL_ENDPOINT = userApi.updateUserSkills;
const USER_EXP_ENDPOINT = userApi.updateUserExperience;
const USER_EDUCATION_ENDPOINT = userApi.updateUserEducation;
const USER_LANGUAGE_ENDPOINT = userApi.updateUserLanguages;

const endpointMapping = new Map<
  (data: TUserInfo) => Promise<AxiosResponse<any, any>>,
  string[]
>([
  [
    USER_PROFILE_ENDPOINT,
    [
      "name",
      "phone",
      "address",
      "avatar",
      "first_name",
      "last_name",
      "display_name",
      "about",
      "location",
    ],
  ],
  [USER_EXP_ENDPOINT, ["experience"]],
  [USER_SKILL_ENDPOINT, ["skills"]],
  [USER_EDUCATION_ENDPOINT, ["education"]],
  [USER_LANGUAGE_ENDPOINT, ["languages"]],
]);

const Profile: React.FC = () => {
  const [isEditProfileVisible, setIsEditProfileVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [userProfile, setUserProfile] = useState(Helpers.getUserInfo());
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const notification = Helpers.notification;
  const { updateUserProfile } = useUser();

  const updateData = async (request: Record<string, any>) => {
    setModalLoading(true);
    const promises = Array.from(endpointMapping.entries())
      .map(([endpointFunc, keys]) => {
        const data = keys.reduce((acc: any, key: any) => {
          if (request[key] !== undefined) {
            acc[key] = request[key];
          }
          return acc;
        }, {} as TUserInfo);

        return Object.keys(data).length > 0 ? endpointFunc(data) : null;
      })
      .filter(Boolean);

    try {
      const results = await Promise.all(promises);
      let summorizeData = {};
      results.forEach((item: any) => {
        if (item.status == 200) {
          summorizeData = { ...summorizeData, ...item.data };
        }
        notification.success("Updated successfully");
      });
      const newExp = await getUserExperience();
      const mergedData = {
        ...userProfile,
        ...summorizeData,
        experience: newExp,
      };
      setUserProfile(mergedData);
      Helpers.storeUserInfo(mergedData);
      updateUserProfile(mergedData);
      setModalLoading(false);
      setIsEditProfileVisible(false);
    } catch (error) {
      console.error("Error updating data", error);
    }
  };

  const getUserExperience = async () => {
    try {
      const response = await userApi.getUserExperience();
      if (response.status == 200) {
        return response.data.experience;
      }
    } catch (error) {
      notification.error("Can not fetch. Please try again");
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(true);
    }, 500);
  }, []);

  return (
    <>
      <div className="Profile">
        <div className="card">
          <div
            className="card-header d-flex align-items-center"
            id="card-profile"
          >
            <div className="group-header">
              <div className="title-group">
                <div className="page-title">Profile</div>
                <div className="page-subtitle">
                  Manage your beautiful profile
                </div>
              </div>
            </div>
          </div>
        </div>
        <Personal
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        <About
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        <Experience
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        <Skill
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        <Education
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        {/* <Certificate
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        /> */}
        <Language
          loading={loading}
          userProfile={userProfile}
          setActiveTab={setActiveTab}
          setIsEditProfileVisible={setIsEditProfileVisible}
        />
        <div className="card card-profile delete-account">
          <button className="delete-action">
            <DeleteOutlined /> Delete Account
          </button>
          <div className="card-title">Delete Account</div>
          <div className="about-description">
            Once you delete your account, it cannot be recovered. Please
            consider this carefully.
          </div>
        </div>
      </div>
      <ProfileModal
        key={isEditProfileVisible ? "open" : "closed"}
        isOpen={isEditProfileVisible}
        cancel={() => setIsEditProfileVisible(false)}
        submit={updateData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        userProfile={userProfile}
        modalLoading={modalLoading}
      />
    </>
  );
};

export default Profile;
